import React from 'react';
import { Edit2, Trash2, Copy, Star, Eye, EyeOff, Languages, Loader2, GripVertical } from 'lucide-react';
import { Button } from '../ui/button';

const ServiceCard = ({ 
  service, 
  onEdit, 
  onDuplicate, 
  onDelete, 
  onToggleActive, 
  onToggleFeatured,
  onAutoTranslate,
  isTranslating,
  isMissingTurkish
}) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div 
      className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-all hover:shadow-md ${
        !service.active ? 'opacity-60' : ''
      } ${isMissingTurkish ? 'border-orange-300' : 'border-gray-200'}`}
      data-testid={`service-card-${service.id}`}
    >
      {/* Image */}
      <div className="relative h-40 bg-gradient-to-br from-blue-100 to-indigo-100">
        {service.image ? (
          <img 
            src={service.image} 
            alt={service.title_en} 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-4xl text-blue-300">📦</span>
          </div>
        )}
        
        {/* Badges */}
        <div className="absolute top-2 left-2 flex gap-2">
          {service.featured && (
            <span className="bg-yellow-400 text-yellow-900 text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
              <Star className="w-3 h-3" /> Featured
            </span>
          )}
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
            service.service_type === 'individual' 
              ? 'bg-emerald-100 text-emerald-700' 
              : 'bg-blue-100 text-blue-700'
          }`}>
            {service.service_type === 'individual' ? 'Individual' : 'Corporate'}
          </span>
        </div>

        {/* Active/Inactive */}
        <div className="absolute top-2 right-2">
          <button
            onClick={() => onToggleActive(service)}
            className={`p-1.5 rounded-full ${service.active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}
            title={service.active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
          >
            {service.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
        </div>

        {/* Missing Turkish warning */}
        {isMissingTurkish && (
          <div className="absolute bottom-2 right-2">
            <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
              <Languages className="w-3 h-3" /> TR Missing
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-semibold text-gray-900 line-clamp-2">
            {service.title_en || 'Untitled Service'}
          </h3>
          <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0 cursor-move" />
        </div>
        
        <p className="text-sm text-gray-500 mb-3 line-clamp-2">
          {service.description_en?.substring(0, 100)}...
        </p>

        <div className="flex items-center justify-between mb-4">
          <span className="text-lg font-bold text-blue-600">
            {formatPrice(service.price)}
          </span>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
            {service.price_type === 'subscription' ? `/${service.subscription_period}` : 'one-time'}
          </span>
        </div>

        {/* Features preview */}
        {service.features_en?.length > 0 && (
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-1">Features ({service.features_en.length})</p>
            <p className="text-xs text-gray-600 line-clamp-1">
              {service.features_en.slice(0, 2).join(', ')}
              {service.features_en.length > 2 && '...'}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(service)}
            className="flex-1"
          >
            <Edit2 className="w-4 h-4 mr-1" /> Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDuplicate(service)}
            className="flex-1"
          >
            <Copy className="w-4 h-4 mr-1" /> Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onToggleFeatured(service)}
            className={service.featured ? 'text-yellow-600 border-yellow-300' : ''}
          >
            <Star className={`w-4 h-4 ${service.featured ? 'fill-yellow-400' : ''}`} />
          </Button>
          {isMissingTurkish && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAutoTranslate(service.id)}
              disabled={isTranslating}
              className="text-orange-600 border-orange-300"
            >
              {isTranslating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Languages className="w-4 h-4" />
              )}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(service.id, service.title_en)}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServiceCard;
